import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from "react";
import {
  computeBudget,
  live,
  todayIso,
  monthOf,
  addMonthsIso,
  type BudgetResult,
  type IsoMonth,
  type Snapshot,
} from "@budgeter/shared";
import type { BudgetStore, SyncStatus } from "./budgetStore";
import type { SessionMarker } from "./session";

export interface AppContextValue {
  store: BudgetStore;
  session: SessionMarker;
  logout: () => Promise<void>;
  /** Called after logging in again following an expired session. */
  relogin: (marker: SessionMarker) => void;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ value, children }: { value: AppContextValue; children: ReactNode }) {
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error("useApp outside AppProvider");
  }
  return ctx;
}

export function useStore(): BudgetStore {
  return useApp().store;
}

export function useSnapshot(): Snapshot {
  const store = useStore();
  return useSyncExternalStore(store.subscribe, () => store.snapshot);
}

export function useSyncStatus(): SyncStatus {
  const store = useStore();
  return useSyncExternalStore(store.subscribeStatus, () => store.status);
}

/** The budget engine over the current snapshot, recomputed only when budget inputs change. */
export function useBudget(throughMonth?: IsoMonth): BudgetResult {
  const s = useSnapshot();
  const through = throughMonth ?? addMonthsIso(monthOf(todayIso()), 1);
  return useMemo(
    () =>
      computeBudget(
        {
          accounts: s.accounts.values(),
          categories: s.categories.values(),
          transactions: s.transactions.values(),
          budgetEntries: s.budgetEntries.values(),
        },
        through,
      ),
    [s.accounts, s.categories, s.transactions, s.budgetEntries, through],
  );
}

export function useLive<T extends keyof Snapshot>(type: T) {
  const s = useSnapshot();
  const map = s[type];
  return useMemo(() => live(s, type), [map]); // eslint-disable-line react-hooks/exhaustive-deps
}
